import { useEffect, useState } from "react";
import {
  AppBorderBox,
  AppBox,
  AppButton,
  AppModal,
  AppPaper,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import { FootballTeamPlayer } from "../../../commons/models/football/football-team-player";
import { IFootballTeamPlayer } from "../../../commons/models/football/interface/i-football-team-player";
import { useSnackbar } from "../../../redux/snackbarProvider";
import {
  fetchManageTeamPlayerList,
  IManageTeamPlayersOption,
} from "../../../redux/slices/football/admin/team/squad/manage-team-players-slice";
import { groupArrayByProperty } from "../../../commons/utilities/array-utils";
import { formatDateTime } from "../../../commons/utilities/date-utils";
import { teamPlayerRemove } from "../../../redux/slices/football/admin/team/squad/manage-team-player-remove-slice";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../commons/components/interface";
import moment from "moment";
import { RolePolicyActionEnum } from "../../../commons/enums/role-policy-action.enum";
import { Can } from "../../../casl/Can";
import IconPersonAdd from "../../../commons/components/icons/person-add";
import { pluralizeString, toSentenceCase } from "../../../commons/utilities/string-utils";
import TeamPlayerCard from "../teams/components/teamPlayer/TeamPlayerCard";
import setDocumentTitle from "../../../commons/utilities/document-title";
import IconEdit from "../../../commons/components/icons/edit";
import IconPersonDelete from "../../../commons/components/icons/person-delete";
import SubTeamPlayerRemove from "../teams/components/teamPlayer/TeamPlayerRemove";
import IconCalendar from "../../../commons/components/icons/calendar";
import SubTeamPlayerRemoveDateAdd from "../teams/components/teamPlayer/TeamPlayerRemoveDateAdd";
import { StatusEnum } from "../../../commons/enums/status-enum";
import SubTeamPlayerRemovalInprogress from "../teams/components/teamPlayer/TeamPlayerRemoveInprogress";
import SubTeamMultiplePlayerAdd from "./components/subTeamPlayer/SubTeamMultiplePlayerAdd";
import {
  fetchManageSubTeamPlayerList,
  IManageSubTeamPlayersOption,
} from "../../../redux/slices/football/admin/team/sub-team/squad/manage-sub-team-players-slice";
import { ManageFootballTeam } from "../../../commons/models/football/admin/manage-football-team";
import SubTeamPlayerEdit from "./components/subTeamPlayer/SubTeamPlayerEdit";
import SubTeamPlayerCard from "./components/subTeamPlayer/SubTeamPlayerCard";
import { subTeamPlayerRemove } from "../../../redux/slices/football/admin/team/sub-team/squad/manage-sub-team-player-remove-slice";

export default function SubTeamManageSquad() {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { subTeamInfo } = useAppSelector((state) => state.footballManageSubTeam);
  const [isPlayerAddEdit, setPlayerAddEdit] = useState(false);
  const [isMultiplePlayerAdd, setMultiplePlayerAdd] = useState(false);
  const [groupedPlayers, setGroupedPlayers] = useState<
    {
      [x: string]: string | FootballTeamPlayer[];
      items: FootballTeamPlayer[];
    }[]
  >([]);
  const [sortedGroupedPlayers, setSortedGroupedPlayers] = useState<
    {
      [x: string]: string | FootballTeamPlayer[];
      items: FootballTeamPlayer[];
    }[]
  >([]);
  const [selectedTeamPlayer, setSelectedTeamPlayer] = useState<IFootballTeamPlayer | null>(null);
  const { manageSubTeamPlayersList, manageSubTeamPlayersListStatus } = useAppSelector(
    (state) => state.footballManageSubTeamPlayers
  );
  const { manageSubTeamPlayerRemoveStatus } = useAppSelector((state) => state.footballManageSubTeamPlayerRemove);
  const [isRemoveTeamPlayer, setRemoveTeamPlayer] = useState(false);
  const [isRemovalConfirm, setRemovalConfirm] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState<FootballTeamPlayer[]>();
  const [minDate, setMinDate] = useState<Date>();

  useEffect(() => {
    if (subTeamInfo.id) {
      const playerListOption: IManageSubTeamPlayersOption = {
        teamId: subTeamInfo.parent.id,
        subTeamId: subTeamInfo.id,
      };
      dispatch(fetchManageSubTeamPlayerList(playerListOption));
    }
  }, [subTeamInfo]);

  useEffect(() => {
    if (manageSubTeamPlayersList.length > 0) {
      setTeamPlayers(manageSubTeamPlayersList.all());
      const formattedPlayers: FootballTeamPlayer[] = manageSubTeamPlayersList.mapArray((player) => ({
        ...player,
        position: player.position,
      }));
      setGroupedPlayers(groupArrayByProperty(formattedPlayers, "position"));
    } else {
      setGroupedPlayers([]);
    }
  }, [manageSubTeamPlayersList]);

  useEffect(() => {
    if (groupedPlayers.length > 0) {
      const customOrder = ["GOALKEEPER", "DEFENDER", "MIDFIELDER", "FORWARD"];
      const sortedData = groupedPlayers.sort((a, b) => {
        return customOrder.indexOf(a.position as string) - customOrder.indexOf(b.position as string);
      });
      setSortedGroupedPlayers(sortedData);
    } else {
      setSortedGroupedPlayers([]);
    }
  }, [groupedPlayers]);

  const handlePlayerRemoveConfirm = (date: Date) => {
    const playerRemovalInfo = {
      endAt: formatDateTime(date, "YYYY-MM-DD HH:mm:00"),
    };
    if (selectedTeamPlayer) {
      setRemovalConfirm(false);
      dispatch(
        subTeamPlayerRemove({
          teamId: subTeamInfo.parent.id,
          subTeamId: subTeamInfo.id,
          playerId: selectedTeamPlayer.player.id,
          playerInfo: playerRemovalInfo,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedTeamPlayer(null);
          addSnackbar({
            key: "player-remove",
            text: "Player Removed Successfully",
            variant: "success",
          });
          const playerListOption: IManageSubTeamPlayersOption = {
            teamId: subTeamInfo.parent.id,
            subTeamId: subTeamInfo.id,
          };
          dispatch(fetchManageSubTeamPlayerList(playerListOption));
          setSelectedTeamPlayer(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  useEffect(() => {
    if (selectedTeamPlayer) {
      const playerStartAt = moment(selectedTeamPlayer.startAt).toDate();
      setMinDate(playerStartAt);
    }
  }, [selectedTeamPlayer]);

  return (
    <>
      <AppBox mb="sm" gap="sm" flexDirection="column">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h5">Squad</AppTitle>
          <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(subTeamInfo.parent)}>
            {manageSubTeamPlayersList.length > 0 && (
              <AppButton label="Add Player" variant="light" onClick={() => setMultiplePlayerAdd(true)} />
            )}
          </Can>
        </AppBox>
        {manageSubTeamPlayersList.length === 0 ? (
          <AppPaper padding="sm" shadow="xs">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="xs">
                  <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                  <AppText fontWeight="semibold">
                    There are not any players in the team.
                    <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(subTeamInfo.parent)}>
                      To add players, click 'Add Player' and select from the list.
                    </Can>
                  </AppText>
                </AppBox>
                <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(subTeamInfo.parent)}>
                  <AppButton label="Add Player" onClick={() => setMultiplePlayerAdd(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <>
            {manageSubTeamPlayersListStatus === StatusEnum.Loading ? (
              <>
                <AppSkeletonGlobalCardList numberOfItems={3} withAvatar />
                <AppSkeletonGlobalCardList numberOfItems={6} withAvatar />
                <AppSkeletonGlobalCardList numberOfItems={5} withAvatar />
                <AppSkeletonGlobalCardList numberOfItems={5} withAvatar />
              </>
            ) : (
              <>
                {sortedGroupedPlayers.map((groupedPlayer, index) => (
                  <AppBox key={index} flexDirection="column" gap="xs">
                    <AppTitle as="h5">{toSentenceCase(pluralizeString(groupedPlayer.position as string))}</AppTitle>
                    <AppBox flexDirection="column" gap="sm">
                      {(groupedPlayer.items as FootballTeamPlayer[]).map((player) => (
                        <SubTeamPlayerCard
                          key={player.id}
                          playerInfo={player}
                          onPlayerRemove={(player) => {
                            setSelectedTeamPlayer(player);
                            setRemoveTeamPlayer(true);
                          }}
                          onPlayerEdit={(player) => {
                            setSelectedTeamPlayer(player);
                            setPlayerAddEdit(true);
                          }}
                        />
                      ))}
                    </AppBox>
                  </AppBox>
                ))}
              </>
            )}
          </>
        )}
      </AppBox>
      <AppModal
        opened={isPlayerAddEdit}
        onClose={() => {
          setSelectedTeamPlayer(null);
          setPlayerAddEdit(false);
          setDocumentTitle([`${subTeamInfo.shortName}`, "Manage"]);
        }}
        titleIcon={selectedTeamPlayer ? <IconEdit /> : <IconPersonAdd />}
        title={selectedTeamPlayer ? "Edit Team Player" : "Add Player To Team"}
        withoutButtons
      >
        <SubTeamPlayerEdit
          subTeam={subTeamInfo}
          existingSubTeamPlayers={teamPlayers}
          selectedSubTeamPlayer={selectedTeamPlayer}
          onPlayerEdit={() => {
            setSelectedTeamPlayer(null);
            setPlayerAddEdit(false);
            setDocumentTitle([`${subTeamInfo.shortName}`, "Manage"]);
          }}
        />
      </AppModal>
      <AppModal
        modalSize="xl"
        opened={isMultiplePlayerAdd}
        onClose={() => {
          setMultiplePlayerAdd(false);
          setDocumentTitle([`${subTeamInfo.shortName}`, "Manage"]);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Add Player To Team"}
        withoutButtons
      >
        <SubTeamMultiplePlayerAdd
          subTeam={subTeamInfo}
          onAdd={() => {
            setMultiplePlayerAdd(false);
            setDocumentTitle([`${subTeamInfo.shortName}`, "Manage"]);
          }}
          onCancel={() => {
            setMultiplePlayerAdd(false);
            setDocumentTitle([`${subTeamInfo.shortName}`, "Manage"]);
          }}
        />
      </AppModal>
      <AppModal
        opened={isRemoveTeamPlayer}
        onClose={() => {
          setRemoveTeamPlayer(false);
          setSelectedTeamPlayer(null);
        }}
        title="Want To Remove This Player?"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        confirmButtonLabel="Remove"
        onSave={() => {
          setRemoveTeamPlayer(false);
          setRemovalConfirm(true);
        }}
      >
        <SubTeamPlayerRemove teamPlayer={selectedTeamPlayer as IFootballTeamPlayer} />
      </AppModal>
      <AppModal
        opened={isRemovalConfirm}
        onClose={(e) => {
          setRemovalConfirm(false);
          setSelectedTeamPlayer(null);
        }}
        title="Add Player's Release Date"
        titleIcon={<IconCalendar />}
        withoutButtons
      >
        <SubTeamPlayerRemoveDateAdd
          minDate={minDate}
          onAdd={(date) => {
            handlePlayerRemoveConfirm(date);
          }}
          onCancel={() => {
            setRemovalConfirm(false);
            setSelectedTeamPlayer(null);
          }}
        />
      </AppModal>
      <AppModal
        opened={manageSubTeamPlayerRemoveStatus === StatusEnum.Loading}
        title="Removing Team"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <SubTeamPlayerRemovalInprogress teamPlayer={selectedTeamPlayer as IFootballTeamPlayer} />
      </AppModal>
    </>
  );
}
